import { useEffect, useMemo, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  getWorkFinancialDashboard,
  type WorkFinancialDashboard,
} from "../../../obras/obrasApi";
import { FinancialKpiCard, formatCurrency, formatPercent, statusClass, statusLabel } from "./FinancialDashboardShared";

interface Props { workId: number; }

export function WorkFinancialDashboardTab({ workId }: Props) {
  const [dashboard, setDashboard] = useState<WorkFinancialDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getWorkFinancialDashboard(workId)
      .then((data) => { if (active) setDashboard(data); })
      .catch((err) => { if (active) setError(err instanceof Error ? err.message : "Erro ao carregar dashboard financeiro."); });
    return () => { active = false; };
  }, [workId]);

  const topSuppliers = useMemo(() => {
    if (!dashboard) return [];
    const grouped = new Map<string, number>();
    for (const entry of dashboard.entries) {
      if (entry.entryType !== "Expense") continue;
      const name = (entry.entityName ?? "(sem entidade)").trim() || "(sem entidade)";
      grouped.set(name, (grouped.get(name) ?? 0) + (entry.totalAmount ?? 0));
    }
    return Array.from(grouped.entries())
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [dashboard]);

  if (error) return <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3">{error}</div>;
  if (!dashboard) return <div className="text-secondary-text py-6">A carregar dashboard financeiro…</div>;

  const totals = dashboard.totals;
  const alertCount = dashboard.alerts.length;
  const statusHint =
    alertCount > 0
      ? `${alertCount} ${alertCount === 1 ? "alerta a resolver" : "alertas a resolver"} — ver em baixo.`
      : dashboard.financialStatus === "Healthy"
        ? "Sem alertas financeiros nesta obra."
        : "Ainda sem dados financeiros suficientes para avaliar.";

  return (
    <div className="space-y-6">
      {/* Saúde financeira: labelled status banner (was a bare, context-less badge). */}
      <div
        className={`border p-4 ${statusClass(dashboard.financialStatus)}`}
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <div style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
          Saúde financeira: {statusLabel(dashboard.financialStatus)}
        </div>
        <div className="text-sm mt-1">{statusHint}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <FinancialKpiCard label="Valor orçamentado" value={formatCurrency(totals.budgetedAmount)} hint={`Base: ${totals.budgetSource}`} />
        <FinancialKpiCard label="Receitas totais" value={formatCurrency(totals.revenueAmount)} hint={`${formatCurrency(totals.pendingRevenueAmount)} por validar`} />
        <FinancialKpiCard label="Despesas totais" value={formatCurrency(totals.expenseAmount)} hint={`${formatCurrency(totals.pendingExpenseAmount)} por validar`} />
        <FinancialKpiCard label="Margem real" value={formatCurrency(totals.realMarginAmount)} hint={formatPercent(totals.realMarginPercent)} />
        <FinancialKpiCard label="Desvio orçamento" value={formatCurrency(totals.revenueDeviationAmount)} hint={formatPercent(totals.revenueDeviationPercent)} />
        <FinancialKpiCard label="Dados por validar" value={formatCurrency(totals.pendingValidationAmount)} hint={`${totals.pendingValidationCount} registo(s)`} />
      </div>

      {dashboard.alerts.length > 0 && (
        <div className="bg-card border border-border p-4" style={{ borderRadius: "var(--radius-card)" }}>
          <h3 className="text-foreground mb-3" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>Alertas</h3>
          <ul className="space-y-2 text-sm">
            {dashboard.alerts.map((alert) => <li key={alert.code} className="text-secondary-text">• {alert.message}</li>)}
          </ul>
        </div>
      )}

      {topSuppliers.length > 0 && (
        <div className="bg-card border border-border" style={{ borderRadius: "var(--radius-card)" }}>
          <div className="px-5 py-4 border-b border-border"><h3 className="text-foreground" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>Top fornecedores (despesa)</h3></div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={Math.max(180, topSuppliers.length * 48)}>
              <BarChart data={topSuppliers} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 12 }} stroke="var(--secondary-text)" />
                <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 12 }} stroke="var(--secondary-text)" />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius)" }} />
                <Bar dataKey="total" name="Despesa" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-card border border-border" style={{ borderRadius: "var(--radius-card)" }}>
        <div className="px-5 py-4 border-b border-border"><h3 className="text-foreground" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>Evolução mensal acumulada</h3></div>
        <div className="p-5">
          {dashboard.monthlySeries.length === 0 ? (
            <p className="py-6 text-center text-secondary-text">Sem evolução mensal.</p>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={dashboard.monthlySeries} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad-revenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad-expense" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad-margin" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="var(--secondary-text)" />
                <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 12 }} stroke="var(--secondary-text)" width={90} />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius)" }} />
                <Legend />
                <Area type="monotone" dataKey="revenueAmount" name="Receitas" stroke="#10b981" fill="url(#grad-revenue)" strokeWidth={2} />
                <Area type="monotone" dataKey="expenseAmount" name="Despesas" stroke="#ef4444" fill="url(#grad-expense)" strokeWidth={2} />
                <Area type="monotone" dataKey="marginAmount" name="Margem" stroke="#0ea5e9" fill="url(#grad-margin)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
